import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import './HamsterChatWindow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPlus, faBell, faPlay } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import notificationService from '../services/notificationService';
import { getAbsoluteMediaUrl } from '../utils/mediaUtils';
import { clickedInAppLink, formatMessageLine } from './hamsterMessageHtml';

const SHOW_MORE = 'Show more';

const HamsterChatWindow = ({ onClose }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [hasMicPermission, setHasMicPermission] = useState(null);
    const [hasMicCapability, setHasMicCapability] = useState(null);
    const [audioStream, setAudioStream] = useState(null);
    const [audioData, setAudioData] = useState([]);
    const [threadId, setThreadId] = useState(uuidv4());
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [audioReady, setAudioReady] = useState(false);
    const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
    const [voiceModeEnabled, setVoiceModeEnabled] = useState(localStorage.getItem('voiceModeEnabled') === 'true');
    const [isAudioPrimed, setIsAudioPrimed] = useState(false);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const animationFrameRef = useRef(null);
    const audioPlayerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const isMountedRef = useRef(true);

    // One AudioContext for the whole component, built on first real need and
    // reused. Browsers hard-cap these (~6 per document) and the old code created
    // a fresh one on every chat open while only ever closing a different one, so
    // roughly the seventh open threw an uncaught DOMException and white-screened
    // the app. Returns null rather than throwing, so a device that refuses audio
    // (e.g. held exclusively by a Teams call) degrades to text-only chat.
    const getAudioContext = useCallback(() => {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            return audioContextRef.current;
        }
        try {
            const Ctor = window.AudioContext || window.webkitAudioContext;
            if (!Ctor) return null;
            audioContextRef.current = new Ctor();
            return audioContextRef.current;
        } catch (error) {
            console.warn('AudioContext unavailable, voice features disabled:', error);
            audioContextRef.current = null;
            return null;
        }
    }, []);

    const { user } = useContext(AuthContext);
    const history = useHistory();

    // Check if device has microphone capability
    useEffect(() => {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            navigator.mediaDevices.enumerateDevices()
                .then(devices => {
                    const hasMic = devices.some(device => device.kind === 'audioinput');
                    setHasMicCapability(hasMic);
                    console.log('Microphone capability:', hasMic);
                })
                .catch(err => {
                    console.error('Error checking microphone capability:', err);
                    setHasMicCapability(false);
                });
        } else {
            setHasMicCapability(false);
            console.log('MediaDevices API not supported');
        }
    }, []);

    // Warm the chat LLM endpoint on open so the first reply doesn't wait on a
    // cold GPU boot. Best-effort and debounced server-side; failure is silent.
    useEffect(() => {
        axiosInstance.post('/api/knowledge-hub/warm-llm/').catch(() => {});
    }, []);

    // Clean up audio resources when component unmounts
    useEffect(() => {
        return () => {
            // Cleanup function that runs when component unmounts
            console.log('Component unmounting, cleaning up audio resources...');
            stopCurrentAudio();
            if (audioStream) {
                audioStream.getTracks().forEach(track => track.stop());
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            isMountedRef.current = false;
        };
    }, []); // Empty dependency array means this only runs on mount/unmount

    // Clean up audio resources when component unmounts or audio stream changes
    useEffect(() => {
        return () => {
            if (audioStream) {
                audioStream.getTracks().forEach(track => track.stop());
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [audioStream]);

    // Add new effect for audio warm-up
    useEffect(() => {
        const warmAudioContext = async () => {
            console.log('Attempting to warm up audio context...');
            try {
                // Create or resume the audio context if needed
                const context = getAudioContext();
                if (!context) {
                    setIsAudioPrimed(false);
                    return;
                }

                if (context.state === 'suspended') {
                    console.log('Resuming suspended AudioContext...');
                    await context.resume();
                }

                // Create and play a silent audio element to unlock autoplay
                console.log('Playing silent audio to unlock autoplay...');
                const silentAudio = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQgAAAAA');
                silentAudio.muted = true;
                silentAudio.autoplay = true;
                
                try {
                    await silentAudio.play();
                    silentAudio.pause();
                    console.log('Successfully warmed up audio context and unlocked autoplay');
                    setIsAudioPrimed(true);
                } catch (error) {
                    console.warn('Failed to warm up audio context:', error);
                    setIsAudioPrimed(false);
                }
            } catch (error) {
                console.error('Error during audio context warm-up:', error);
                setIsAudioPrimed(false);
            }
        };

        // Add click listener for first user interaction
        if (!isAudioPrimed && voiceModeEnabled) {
            console.log('Adding click listener for audio warm-up...');
            const handleFirstClick = async () => {
                await warmAudioContext();
                document.removeEventListener('click', handleFirstClick);
            };
            document.addEventListener('click', handleFirstClick, { once: true });
            
            return () => {
                document.removeEventListener('click', handleFirstClick);
            };
        }
    }, [getAudioContext, voiceModeEnabled, isAudioPrimed]);

    const dealOptions = [
        { text: "Deals, Recaps, Deliveries relationship", prompt: "Give me a user friendly rundown of how deals, recaps and deliveries are linked with an example" },
        { text: "Most shipped products", prompt: "What are the most shipped products and their quantities in my recaps?" },
        { text: "Shipping ports", prompt: "Give me a deep analysis of my top three shipping ports and the coal being shipped" },
        { text: "Vessel Rundown", prompt: "Give me a rundown of my vessels and their associated data" }
    ];

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        initializeNewChat();
    }, []);

    // Fetch notification stats for bell icon
    useEffect(() => {
        const fetchNotificationStats = async () => {
            try {
                const stats = await notificationService.getStats();
                setUnreadNotificationCount(stats.unread || 0);
            } catch (error) {
                console.error('Error fetching notification stats:', error);
                setUnreadNotificationCount(0);
            }
        };

        fetchNotificationStats();

        // Refresh counts every 30 seconds
        const intervalId = setInterval(fetchNotificationStats, 30000);

        // Listen for immediate updates when notifications are marked read/unread
        const handleNotificationChange = () => {
            fetchNotificationStats();
        };
        
        window.addEventListener('notificationStatsChanged', handleNotificationChange);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('notificationStatsChanged', handleNotificationChange);
        };
    }, []);

    const handleNewChat = () => {
        stopCurrentAudio(); // Stop any playing audio
        setMessages([]);
        setInputMessage('');
        setThreadId(uuidv4());
        initializeNewChat();
    };

    const handleClose = () => {
        stopCurrentAudio(); // Stop any playing audio
        onClose(); // Call the original onClose handler
    };

    const handleNotificationClick = () => {
        stopCurrentAudio(); // Stop any playing audio
        onClose(); // Close the chat window
        history.push('/settings/notifications'); // Navigate to notifications screen
    };

    const initializeNewChat = async () => {
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        if (!isLoggedIn || !user?.id) return;

        try {
            const formData = new FormData();
            formData.append('input', '');
            formData.append('new_chat', 'true');
            formData.append('thread_id', threadId);

            await axiosInstance.post(
                '/api/offer/hms-interrogation/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
        } catch (error) {
            console.error('Error initializing new chat:', error);
        }
    };

    // A quick prompt, or the Show more button: sent as typed, at once.
    const handlePrompt = async (prompt) => {
        setInputMessage(prompt);
        handleSendMessage(null, prompt);
    };

    const handleVoiceSelection = (voice) => {
        setSelectedVoice(voice);
        localStorage.setItem('selectedVoice', voice);
        setShowVoiceModal(false);
        startRecording();
    };

    const getSelectedVoice = () => {
        return selectedVoice || localStorage.getItem('selectedVoice') || 'am_echo';
    };

    const stopCurrentAudio = () => {
        if (audioPlayerRef.current) {
            console.log('Stopping current audio playback...');
            audioPlayerRef.current.pause();
            audioPlayerRef.current.src = '';
            if (currentAudioUrl) {
                URL.revokeObjectURL(currentAudioUrl);
            }
            audioPlayerRef.current = null;
            setCurrentAudioUrl(null);
            setAudioReady(false);
        }
    };

    const toggleRecording = async () => {
        if (isRecording) {
            stopRecording();
        } else {
            // Always stop any playing audio before starting new recording
            stopCurrentAudio();

            if (!selectedVoice) {
                setShowVoiceModal(true);
            } else {
                try {
                    // Only try to initialize audio if not already primed
                    if (voiceModeEnabled && !isAudioPrimed) {
                        console.log('Audio not primed, attempting to prime...');
                        const context = getAudioContext();
                        if (context) {
                            await context.resume();
                            setIsAudioPrimed(true);
                        }
                    }
                    
                    // Proceed with recording
                    await startRecording();
                    
                } catch (error) {
                    console.error('Error in toggleRecording:', error);
                    // Still try to start recording even if audio initialization fails
                    await startRecording();
                }
            }
        }
    };

    const startRecording = async () => {
        try {
            console.log('Starting audio recording setup...');
            // Request microphone permission
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setAudioStream(stream);
            setHasMicPermission(true);
            
            // Reuse the shared context. Creating one per recording is what used
            // to exhaust the browser's context cap.
            const context = getAudioContext();
            if (!context) {
                throw new Error('Audio is unavailable on this device');
            }
            if (context.state === 'suspended') {
                await context.resume();
            }

            const analyser = context.createAnalyser();
            analyser.fftSize = 256;
            analyserRef.current = analyser;

            const source = context.createMediaStreamSource(stream);
            source.connect(analyser);
            
            // Set up media recorder
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                audioChunksRef.current = [];
                processAudioData(audioBlob);
            };
            
            // Start recording immediately
            console.log('Starting media recorder...');
            mediaRecorder.start();
            setIsRecording(true);
            
            // Add initial recording message with delayed visualization
            setTimeout(() => {
                console.log('Adding recording message and starting visualization...');
                setMessages(prev => [...prev, { 
                    type: 'user', 
                    text: 'Processing audio...',
                    timestamp: new Date().toISOString()
                }]);
                // Start visualizing audio after delay
                visualizeAudio();
            }, 300);
            
        } catch (error) {
            console.error('Error starting recording:', error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setHasMicPermission(false);
                alert('Microphone permission denied. Please allow microphone access to use this feature.');
            } else {
                alert('Error starting recording. Please try again.');
            }
        }
    };
    
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            
            if (audioStream) {
                audioStream.getTracks().forEach(track => track.stop());
                setAudioStream(null);
            }
            
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            // Drop the analyser but KEEP the context alive: it is shared for the
            // life of the component and closed once, on unmount.
            if (analyserRef.current) {
                analyserRef.current.disconnect();
            }
            analyserRef.current = null;
            
            setIsRecording(false);
            setAudioData([]);
        }
    };
    
    const visualizeAudio = () => {
        if (!analyserRef.current) return;
        
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        const updateVisualization = () => {
            if (!analyserRef.current) return;
            
            analyserRef.current.getByteFrequencyData(dataArray);
            
            // We'll use a subset of the frequency data for visualization
            const visualData = Array.from(dataArray.slice(0, 20))
                .map(value => value / 255); // Normalize to 0-1
                
            setAudioData(visualData);
            
            animationFrameRef.current = requestAnimationFrame(updateVisualization);
        };
        
        updateVisualization();
    };
    
    const playAudioResponse = (base64Audio) => {
        try {
            if (!base64Audio) {
                console.error('No audio data received');
                return;
            }
            console.log('Processing audio response, length:', base64Audio.length);

            // Convert base64 to blob with detailed logging
            const binaryString = window.atob(base64Audio);
            console.log('Converted to binary string, length:', binaryString.length);
            
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            console.log('Converted to bytes array, length:', bytes.length);

            const audioBlob = new Blob([bytes], { type: 'audio/wav' });
            console.log('Created audio blob, size:', audioBlob.size);

            // Clean up existing audio resources
            if (audioPlayerRef.current) {
                console.log('Cleaning up previous audio resources...');
                audioPlayerRef.current.pause();
                audioPlayerRef.current.src = '';
                if (currentAudioUrl) {
                    URL.revokeObjectURL(currentAudioUrl);
                    console.log('Revoked previous audio URL');
                }
            }

            // Create and configure new audio element
            const audio = new Audio();
            audio.autoplay = true; // Enable autoplay attribute
            audioPlayerRef.current = audio;
            const audioUrl = URL.createObjectURL(audioBlob);
            setCurrentAudioUrl(audioUrl);
            console.log('Created new audio URL:', audioUrl);

            // Configure audio element with comprehensive event handling
            audio.addEventListener('loadstart', () => console.log('Audio loading started'));
            audio.addEventListener('loadeddata', async () => {
                console.log('Audio data loaded successfully');
                if (voiceModeEnabled && isAudioPrimed) {
                    try {
                        await audio.play();
                        console.log('Autoplay started successfully');
                    } catch (error) {
                        console.warn('Autoplay failed, falling back to manual play:', error);
                        setAudioReady(true); // Enable manual play button
                    }
                } else {
                    console.log('Voice mode disabled or audio not primed, enabling manual play button');
                    setAudioReady(true);
                }
            });

            audio.addEventListener('playing', () => console.log('Audio playback started'));
            audio.addEventListener('pause', () => console.log('Audio playback paused'));
            audio.addEventListener('ended', () => {
                console.log('Audio playback completed');
                setAudioReady(false);
            });

            audio.addEventListener('error', (e) => {
                console.error('Audio playback error:', e);
                if (audio.error) {
                    console.error('Audio error details:', {
                        code: audio.error.code,
                        message: audio.error.message
                    });
                }
                setAudioReady(true); // Enable manual play button on error
            });

            // Set audio properties and load
            audio.preload = 'auto';
            audio.src = audioUrl;

        } catch (error) {
            console.error('Error in playAudioResponse:', error);
            console.error('Error stack:', error.stack);
            setAudioReady(true); // Enable manual play button on error
        }
    };

    const handlePlayAudio = () => {
        if (audioPlayerRef.current) {
            audioPlayerRef.current.play()
                .catch(error => {
                    console.error('Playback failed:', error);
                });
        }
    };

    const processAudioData = async (audioBlob) => {
        try {
            setIsLoading(true);
            
            // Create new abort controller for this request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            
            // Stop any currently playing audio
            stopCurrentAudio();
            
            // Create form data to send to backend
            const formData = new FormData();
            formData.append('voice_file', audioBlob, 'recording.wav');
            formData.append('thread_id', threadId);
            formData.append('voice_preference', getSelectedVoice());
            
            // Send the audio file to the backend with signal
            const response = await axiosInstance.post(
                '/api/offer/hms-interrogation/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    signal: abortControllerRef.current.signal
                }
            );
            
            // Only process response if component is still mounted
            if (!isMountedRef.current) return;
            
            // Update the last message (which should be the "Processing audio..." message)
            setMessages(prev => {
                const newMessages = [...prev];
                if (newMessages.length > 0) {
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage.text === 'Processing audio...') {
                        lastMessage.text = response.data.transcribed_text || 'Could not transcribe audio';
                    }
                }
                return newMessages;
            });
            
            // Add the assistant's response if present
            if (response.data.message) {
                setMessages(prev => [...prev, {
                    type: 'assistant',
                    text: response.data.message,
                    timestamp: new Date().toISOString(),
                    show_more: !!response.data.show_more,
                }]);

                // Only play audio if component is still mounted
                if (isMountedRef.current && response.data.audio_response) {
                    playAudioResponse(response.data.audio_response);
                }
            }
            
        } catch (error) {
            if (!isMountedRef.current) return;
            
            // Only show error if it's not an abort error
            if (error.name !== 'AbortError') {
                console.error('Error processing audio:', error);
                setMessages(prev => [...prev, { 
                    type: 'assistant', 
                    text: error.response?.data?.error || 'Error processing audio. Please try again.',
                    timestamp: new Date().toISOString()
                }]);
            }
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const handleSendMessage = async (e, textOverride) => {
        e?.preventDefault();
        const outgoing = typeof textOverride === 'string' ? textOverride : inputMessage;
        if (!outgoing.trim()) return;

        // Check if user is logged in using the isLoggedIn flag instead of token
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        if (!isLoggedIn || !user?.id) {
            console.error('User is not authenticated');
            setMessages(prev => [...prev, { 
                type: 'assistant', 
                text: 'Please log in to continue.',
                timestamp: new Date().toISOString()
            }]);
            return;
        }

        try {
            setIsLoading(true);
            setMessages(prev => [...prev, { 
                type: 'user', 
                text: outgoing,
                timestamp: new Date().toISOString()
            }]);

            const formData = new FormData();
            formData.append('input', outgoing);
            formData.append('thread_id', threadId);
            formData.append('voice_preference', getSelectedVoice());

            const response = await axiosInstance.post(
                '/api/offer/hms-interrogation/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setMessages(prev => [...prev, { 
                type: 'assistant', 
                text: response.data.message,
                timestamp: new Date().toISOString(),
                has_training_videos: response.data.has_training_videos || false,
                training_videos: response.data.training_videos || null,
                // A partner list with more rows than shown: the reply gets a Show more button.
                show_more: !!response.data.show_more,
            }]);

            // Play audio response if available
            if (response.data.audio_response) {
                playAudioResponse(response.data.audio_response);
            }
            
            setInputMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => [...prev, { 
                type: 'assistant', 
                text: error.response?.data?.error || 'Sorry, there was an error processing your request.',
                timestamp: new Date().toISOString()
            }]);
        }
        setIsLoading(false);
    };

    // A link in a reply to a page of this system opens in place, no reload.
    const handleMessagesClick = (e) => {
        const href = clickedInAppLink(e);
        if (!href) return;
        e.preventDefault();
        history.push(href);
    };

    const handleInputKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    return (
        <div className="hamstir-popup-window">
            <div className="hamstir-popup-header">
                <button onClick={handleClose} className="hamstir-popup-close" aria-label="Close chat">
                    ×
                </button>
                <h2>HAMSTIR</h2>
                <div className="hamstir-popup-header-actions">
                    <button 
                        onClick={handleNotificationClick} 
                        className={`hamstir-popup-notification-bell ${unreadNotificationCount > 0 ? 'has-unread' : ''}`}
                        aria-label={`Notifications ${unreadNotificationCount > 0 ? `- ${unreadNotificationCount} unread` : ''}`}
                    >
                        <FontAwesomeIcon icon={faBell} />
                    </button>
                    <button onClick={handleNewChat} className="hamstir-popup-new-chat" aria-label="Start new chat">
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                </div>
            </div>

            <div className="hamstir-popup-messages" onClick={handleMessagesClick}>
                {messages.length === 0 && !isLoading && !inputMessage && !isRecording && (
                    <div className="hamstir-popup-placeholder">
                        <div className="hamstir-popup-placeholder-content">
                            <p>Select an option to analyze your data</p>
                            <div className="hamstir-popup-options-grid">
                                {dealOptions.map((option, index) => (
                                    <div
                                        key={index}
                                        className="hamstir-popup-option"
                                        onClick={() => handlePrompt(option.prompt)}
                                    >
                                        <span>{option.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                
                {messages.map((message, index) => {
                    // Only the newest reply offers Show more: an older one would
                    // ask for a page the thread has already moved past.
                    const isNewestReply = message.type === 'assistant'
                        && index === messages.map((m) => m.type).lastIndexOf('assistant');
                    return (
                        <div key={index} className={`hamstir-popup-message ${message.type}`}>
                            {/* Training video cards FIRST (if present) */}
                            {message.has_training_videos && message.training_videos && message.training_videos.length > 0 && (
                                <div className="hamstir-training-videos">
                                    {message.training_videos.map((video, vIdx) => (
                                        <div 
                                            key={vIdx} 
                                            className="hamstir-training-video-card"
                                            onClick={() => {
                                                // Close chat and navigate to training screen with video ID
                                                onClose();
                                                history.push(`/training?videoId=${video.video_id}&autoplay=true`);
                                            }}
                                        >
                                            {video.thumbnail_url && (
                                                <img 
                                                    src={getAbsoluteMediaUrl(video.thumbnail_url)} 
                                                    alt={video.title}
                                                    className="hamstir-training-video-thumbnail"
                                                    onError={(e) => {
                                                        console.error('Failed to load thumbnail:', video.thumbnail_url);
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            <div className="hamstir-training-video-info">
                                                <h4>{video.title}</h4>
                                                <p className="hamstir-training-video-category">{video.category}</p>
                                                <p className="hamstir-training-video-duration">
                                                    {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')} min
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Natural language message AFTER videos */}
                            {message.text.split('\n').map((line, i) => (
                                <p 
                                    key={i} 
                                    dangerouslySetInnerHTML={{ __html: formatMessageLine(line) }}
                                />
                            ))}
                            
                            {isNewestReply && message.show_more && !isLoading && (
                                <button
                                    type="button"
                                    onClick={() => handlePrompt(SHOW_MORE)}
                                    className="hamstir-popup-show-more"
                                >
                                    {SHOW_MORE}
                                </button>
                            )}

                            {message.type === 'assistant' && !voiceModeEnabled && audioReady && (
                                <button 
                                    onClick={handlePlayAudio}
                                    className="hamstir-popup-play-audio"
                                >
                                    Play Audio
                                </button>
                            )}
                            <div className="hamstir-popup-timestamp">
                                {new Date(message.timestamp).toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit', 
                                    hour12: false 
                                })}
                            </div>
                        </div>
                    );
                })}
                {isLoading && (
                    <div className="hamstir-popup-message assistant">
                        <div className="hamstir-popup-typing">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="hamstir-popup-input-container">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleInputKeyPress}
                    placeholder="Ask HAMSTIR Assistant..."
                    disabled={isLoading}
                    className="hamstir-popup-input"
                />
                <button 
                    type="submit" 
                    className="hamstir-popup-send"
                    disabled={isLoading || !inputMessage.trim()}
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </form>
        </div>
    );
};

export default HamsterChatWindow;